const inspectRenderReference = Symbol("inspect Vallum render reference");
const consumeRenderReference = Symbol("consume Vallum render reference");
/**
 * A protected value delivered as pixels rather than text.
 *
 * The plaintext was rendered by the proxy and never sent as a string. Nothing
 * in this object, the DOM, or the accessibility tree contains it. Reading it
 * requires capturing the painted canvas and running OCR.
 *
 * `toString()` deliberately yields a mask rather than throwing, so accidental
 * interpolation renders a placeholder instead of crashing or leaking.
 */
export class VallumRenderRef {
    kind = "vallum-render-ref";
    #payload;
    constructor(payload) {
        this.#payload = payload;
    }
    get width() { return this.#payload?.width ?? 0; }
    get height() { return this.#payload?.height ?? 0; }
    get consumed() { return this.#payload === undefined; }
    /** Returns the payload once and releases it. */
    take() {
        const payload = this.#payload;
        this.#payload = undefined;
        return payload;
    }
    /** @internal Reads pixels without consuming them while an async bitmap is prepared. */
    [inspectRenderReference]() {
        return this.#payload;
    }
    /** @internal Atomically consumes the payload used to prepare a completed paint. */
    [consumeRenderReference](expected) {
        if (this.#payload !== expected)
            return false;
        this.#payload = undefined;
        return true;
    }
    toString() { return "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"; }
    toJSON() { return this.toString(); }
}
/**
 * A normal Response whose direct `json()` call can return one-shot render
 * references without serializing their pixel payload back through text.
 *
 * The actual body remains valid, masked JSON for logging, text readers, and
 * clones that are not parsed through this class. Calling `json()` still
 * consumes the body exactly once, matching the platform Response contract.
 */
class VallumReconstructedResponse extends Response {
    #document;
    #bodyText;
    #sourceURL;
    #sourceType;
    #sourceRedirected;
    constructor(document, bodyText, init, source) {
        super(bodyText, init);
        this.#document = document;
        this.#bodyText = bodyText;
        this.#sourceURL = source.url;
        this.#sourceType = source.type;
        this.#sourceRedirected = source.redirected;
    }
    get url() { return this.#sourceURL; }
    get type() { return this.#sourceType; }
    get redirected() { return this.#sourceRedirected; }
    async json() {
        await super.text();
        return this.#document;
    }
    clone() {
        if (this.bodyUsed || this.body?.locked)
            throw new TypeError("Body has already been consumed.");
        return new VallumReconstructedResponse(cloneReconstructedDocument(this.#document), this.#bodyText, {
            status: this.status,
            statusText: this.statusText,
            headers: this.headers,
        }, this);
    }
}
function cloneReconstructedDocument(value) {
    // Container graphs follow native clone/json independence, but one-shot
    // pixel references are intentionally shared. Cloning a Response must not
    // duplicate protected pixels or defeat consumption and heap release.
    if (value instanceof VallumRenderRef)
        return value;
    if (Array.isArray(value))
        return value.map(cloneReconstructedDocument);
    if (!isRecord(value))
        return value;
    const clone = {};
    for (const key of Object.keys(value)) {
        Object.defineProperty(clone, key, {
            configurable: true,
            enumerable: true,
            writable: true,
            value: cloneReconstructedDocument(value[key]),
        });
    }
    return clone;
}
function isRenderPayload(value) {
    return isRecord(value) && typeof value.png === "string" && value.png.length > 0 &&
        typeof value.width === "number" && typeof value.height === "number";
}
const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });
const unsignedPayloadMarker = "UNSIGNED-PAYLOAD";
const requestProofDomain = "vallum-request-proof-v1.";
const deferredRefKey = "$vallum_ref";
const disclosurePath = "/__vallum/disclose";
const disclosureBatchSize = 64;
const maxDeferredReferences = 256;
const responseReplayWindow = 1_024;
/**
 * Returns the handle when the node is exactly `{"$vallum_ref": "<handle>"}`.
 * Requiring a sole member keeps an application object that happens to contain
 * the key alongside real data from being mistaken for a placeholder.
 */
function deferredHandleOf(value) {
    if (!isRecord(value))
        return undefined;
    const keys = Object.keys(value);
    if (keys.length !== 1 || keys[0] !== deferredRefKey)
        return undefined;
    const handle = value[deferredRefKey];
    return typeof handle === "string" && handle.length > 0 ? handle : undefined;
}
/**
 * Walks the reconstructed document for withheld-value placeholders, recording
 * how to write each one back in place. Bounded so a malformed document cannot
 * drive unbounded work in the browser.
 */
function collectDeferredReferences(node, out, depth = 0) {
    if (depth > 64)
        throw new Error("Vallum protected response nesting exceeds the supported limit");
    if (Array.isArray(node)) {
        for (let index = 0; index < node.length; index++) {
            const child = node[index];
            const handle = deferredHandleOf(child);
            if (handle !== undefined) {
                if (out.length >= maxDeferredReferences) {
                    throw new Error("Vallum protected response has too many deferred values");
                }
                out.push({ handle, assign: (value) => { node[index] = value; } });
                continue;
            }
            collectDeferredReferences(child, out, depth + 1);
        }
        return;
    }
    if (!isRecord(node))
        return;
    for (const key of Object.keys(node)) {
        const child = node[key];
        const handle = deferredHandleOf(child);
        if (handle !== undefined) {
            if (out.length >= maxDeferredReferences) {
                throw new Error("Vallum protected response has too many deferred values");
            }
            out.push({ handle, assign: (value) => { node[key] = value; } });
            continue;
        }
        collectDeferredReferences(child, out, depth + 1);
    }
}
export async function createVallumClient(options) {
    const client = new BrowserVallumClient(options);
    await client.renew();
    return client;
}
class BrowserVallumClient {
    endpoint;
    baseFetch;
    sessionPath;
    challengePath;
    admissionPath;
    renewalWindowMs;
    state;
    renewal;
    isDestroyed = false;
    abortController = new AbortController();
    activeExchanges = 0;
    idleResolvers = new Set();
    replayByEpoch = new Map();
    constructor(options) {
        if (!options.endpoint)
            throw new Error("Vallum endpoint is required");
        if (typeof globalThis.fetch !== "function" && !options.fetch) {
            throw new Error("Vallum requires a browser-compatible fetch implementation");
        }
        if (!globalThis.crypto?.subtle) {
            throw new Error("Vallum requires Web Crypto in a secure browser context");
        }
        this.endpoint = options.endpoint.replace(/\/$/, "");
        this.baseFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
        this.sessionPath = options.sessionPath ?? "/__vallum/session";
        this.challengePath = options.challengePath ?? "/__vallum/challenge";
        this.admissionPath = options.admissionPath ?? "/.well-known/vallum/admission";
        this.renewalWindowMs = options.renewalWindowMs ?? 5_000;
    }
    async renew() {
        this.assertActive();
        if (this.renewal)
            return this.renewal;
        this.renewal = this.establishSessionWhenIdle().finally(() => {
            this.renewal = undefined;
        });
        return this.renewal;
    }
    wrapFetch(implementation) {
        this.assertActive();
        return ((input, init) => this.protectedFetch(implementation, input, init));
    }
    fetch = (input, init) => {
        this.assertActive();
        return this.protectedFetch(this.baseFetch, input, init);
    };
    get destroyed() {
        return this.isDestroyed;
    }
    destroy() {
        if (this.isDestroyed)
            return;
        this.isDestroyed = true;
        this.abortController.abort(new Error("Vallum client has been destroyed"));
        this.state = undefined;
        this.replayByEpoch.clear();
        for (const resolve of this.idleResolvers)
            resolve();
        this.idleResolvers.clear();
    }
    async establishSessionWhenIdle() {
        if (this.activeExchanges > 0) {
            await new Promise((resolve) => this.idleResolvers.add(resolve));
        }
        this.assertActive();
        await this.establishSession();
    }
    async establishSession() {
        this.assertActive();
        const keyPair = await crypto.subtle.generateKey({ name: "RSA-OAEP", modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: "SHA-256" }, false, ["encrypt", "decrypt"]);
        const publicKey = await crypto.subtle.exportKey("jwk", keyPair.publicKey);
        const proofKeyPair = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, ["sign", "verify"]);
        const proofPublicKey = await crypto.subtle.exportKey("jwk", proofKeyPair.publicKey);
        const challengeResponse = await this.baseFetch(new URL(this.challengePath, this.endpoint), {
            method: "POST",
            redirect: "error",
            signal: this.abortController.signal,
            credentials: "include",
            cache: "no-store",
            headers: { Accept: "application/json" },
        });
        this.assertActive();
        if (!challengeResponse.ok)
            throw new Error(`Vallum session challenge failed (${challengeResponse.status})`);
        const challenge = (await challengeResponse.json());
        this.assertActive();
        validateChallenge(challenge);
        let admission = "";
        if (challenge.admission_required) {
            const admissionResponse = await this.baseFetch(new URL(this.admissionPath, this.endpoint), {
                method: "POST",
                redirect: "error",
                signal: this.abortController.signal,
                credentials: "include",
                cache: "no-store",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ public_key: publicKey, proof_key: proofPublicKey, session: challenge.session }),
            });
            this.assertActive();
            if (!admissionResponse.ok)
                throw new Error(`Vallum decoding admission failed (${admissionResponse.status})`);
            const issued = (await admissionResponse.json());
            this.assertActive();
            if (!issued || typeof issued.admission !== "string" || issued.admission.length < 16) {
                throw new Error("Vallum decoding admission response is invalid");
            }
            admission = issued.admission;
        }
        const response = await this.baseFetch(new URL(this.sessionPath, this.endpoint), {
            method: "POST",
            redirect: "error",
            signal: this.abortController.signal,
            credentials: "include",
            cache: "no-store",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ public_key: publicKey, proof_key: proofPublicKey, admission }),
        });
        this.assertActive();
        if (!response.ok)
            throw new Error(`Vallum session bootstrap failed (${response.status})`);
        const bootstrap = (await response.json());
        this.assertActive();
        validateBootstrap(bootstrap);
        const label = encoder.encode(`vallum-session-v1:${bootstrap.session}:${bootstrap.key_id}`);
        const rawKey = await crypto.subtle.decrypt({ name: "RSA-OAEP", label }, keyPair.privateKey, decodeBase64URL(bootstrap.wrapped_key));
        const key = await crypto.subtle.importKey("raw", rawKey, { name: "AES-GCM" }, false, ["decrypt"]);
        this.assertActive();
        this.state = { ...bootstrap, key, proofKey: proofKeyPair.privateKey, expiresAtMs: Date.parse(bootstrap.expires_at) };
        this.replayByEpoch.set(bootstrap.key_id, { seen: new Set(), pending: new Set(), highest: 0 });
        while (this.replayByEpoch.size > 3)
            this.replayByEpoch.delete(this.replayByEpoch.keys().next().value);
    }
    async protectedFetch(implementation, input, init) {
        this.assertActive();
        let responseState = await this.acquireResponseState();
        let ownsExchangeSlot = true;
        let exchange;
        try {
            exchange = await this.performSignedFetch(implementation, input, init, responseState);
            if ((exchange.response.status === 409 || exchange.response.status === 428) && exchange.response.headers.get("X-Vallum-Proof-Required") === "1" && isSafeRetry(exchange.request.method)) {
                exchange.dispose();
                exchange = undefined;
                this.releaseExchangeSlot();
                ownsExchangeSlot = false;
                await this.renew();
                responseState = await this.acquireResponseState(true);
                ownsExchangeSlot = true;
                exchange = await this.performSignedFetch(implementation, input, init, responseState);
            }
            const reconstructed = await this.reconstructIfPresent(exchange.response, responseState, exchange.request.signal);
            this.assertActive();
            return reconstructed;
        }
        finally {
            exchange?.dispose();
            if (ownsExchangeSlot)
                this.releaseExchangeSlot();
        }
    }
    /**
     * Starts an exchange only when no key rotation is running. The synchronous
     * increment closes the race with a concurrent explicit `renew()` call.
     */
    async acquireResponseState(acceptFreshState = false) {
        let refreshed = acceptFreshState;
        while (true) {
            this.assertActive();
            if (this.renewal) {
                await this.renewal;
                refreshed = true;
                continue;
            }
            if (!this.state || (!refreshed && Date.now() + this.renewalWindowMs >= this.state.expiresAtMs)) {
                await this.renew();
                refreshed = true;
                continue;
            }
            this.activeExchanges++;
            return this.state;
        }
    }
    releaseExchangeSlot() {
        if (this.activeExchanges === 0)
            return;
        this.activeExchanges--;
        if (this.activeExchanges !== 0)
            return;
        for (const resolve of this.idleResolvers)
            resolve();
        this.idleResolvers.clear();
    }
    async performSignedFetch(implementation, input, init, state) {
        const built = await buildRequest(input, init, this.endpoint, state);
        this.assertActive();
        const guarded = guardRequestSignal(built, this.abortController.signal);
        try {
            const response = await implementation(guarded.request);
            this.assertActive();
            return { request: guarded.request, response, dispose: guarded.dispose };
        }
        catch (error) {
            guarded.dispose();
            throw error;
        }
    }
    async reconstructIfPresent(response, state, requestSignal) {
        const contentType = response.headers.get("Content-Type")?.toLowerCase() ?? "";
        if (!contentType.includes("application/json") && !contentType.includes("+json"))
            return response;
        let transport;
        try {
            transport = JSON.parse(await response.clone().text());
        }
        catch {
            assertNotAborted(requestSignal);
            return response;
        }
        assertNotAborted(requestSignal);
        this.assertActive();
        if (!isRecord(transport) || !Object.prototype.hasOwnProperty.call(transport, state.metadata_key))
            return response;
        return this.reconstruct(response, state, transport, requestSignal);
    }
    async reconstruct(response, state, transport, requestSignal) {
        const metadata = transport[state.metadata_key];
        const envelope = parseEnvelope(metadata, state.profile);
        if (envelope.version !== 1 || envelope.session !== state.session || envelope.keyID !== state.key_id) {
            throw new Error("Vallum protected response has the wrong session or key epoch");
        }
        if (envelope.expiresMS <= Date.now() || envelope.expiresMS > state.expiresAtMs + 1_000) {
            throw new Error("Vallum protected response has expired");
        }
        const replay = this.replayByEpoch.get(state.key_id) ?? { seen: new Set(), pending: new Set(), highest: 0 };
        this.replayByEpoch.set(state.key_id, replay);
        const replayFloor = replay.highest - responseReplayWindow;
        if (envelope.counter <= replayFloor || replay.seen.has(envelope.counter) || replay.pending.has(envelope.counter)) {
            throw new Error("Vallum protected response replay detected");
        }
        replay.pending.add(envelope.counter);
        let original;
        try {
            const plaintext = await crypto.subtle.decrypt({
                name: "AES-GCM",
                iv: decodeBase64URL(envelope.nonce),
                additionalData: additionalData(envelope, state.profile),
                tagLength: 128,
            }, state.key, decodeBase64URL(envelope.ciphertext));
            original = JSON.parse(decoder.decode(plaintext));
            this.commitCounter(replay, envelope.counter);
        }
        catch (error) {
            if (error instanceof Error && error.message.includes("replay"))
                throw error;
            throw new Error("Vallum response integrity validation failed", { cause: error });
        }
        finally {
            replay.pending.delete(envelope.counter);
        }
        assertNotAborted(requestSignal);
        this.assertActive();
        // Redemption happens after integrity validation and outside its error
        // handling. A disclosure failure is an authorization or budget outcome,
        // not a tampered response, and must not be reported as one.
        const restored = await this.restoreWithheldValues(original, state, requestSignal);
        assertNotAborted(requestSignal);
        this.assertActive();
        const headers = new Headers(response.headers);
        for (const name of ["Content-Length", "Content-Encoding", "X-Vallum-Transform"])
            headers.delete(name);
        headers.set("Content-Type", "application/json; charset=utf-8");
        const body = JSON.stringify(restored);
        return new VallumReconstructedResponse(restored, body, {
            status: response.status,
            statusText: response.statusText,
            headers,
        }, response);
    }
    /**
     * Restores values the proxy withheld from the response.
     *
     * The protected document carries `{"$vallum_ref": handle}` in place of each
     * withheld value. Those values were never sent: they are absent from the
     * body and from the ciphertext, so this is the only way to obtain them. Each
     * redemption spends the session's server-side disclosure budget.
     *
     * Redemption is transparent to the application. It resolves before the
     * Response is constructed, so calling code receives the exact origin object
     * and never observes a handle.
     */
    async restoreWithheldValues(document, state, requestSignal) {
        // A withheld value at the document root has no parent to assign into, so
        // it is handled before the walk.
        const rootHandle = deferredHandleOf(document);
        if (rootHandle !== undefined) {
            const rootValues = await this.redeemHandles([rootHandle], state, requestSignal);
            if (!Object.prototype.hasOwnProperty.call(rootValues, rootHandle)) {
                throw new Error("Vallum could not restore a withheld value; the disclosure budget may be exhausted");
            }
            return rootValues[rootHandle];
        }
        const references = [];
        collectDeferredReferences(document, references);
        if (references.length === 0)
            return document;
        const handles = [...new Set(references.map((reference) => reference.handle))];
        const values = await this.redeemHandles(handles, state, requestSignal);
        for (const reference of references) {
            if (!Object.prototype.hasOwnProperty.call(values, reference.handle)) {
                throw new Error("Vallum could not restore a withheld value; the disclosure budget may be exhausted");
            }
            reference.assign(values[reference.handle]);
        }
        return document;
    }
    async redeemHandles(handles, state, requestSignal) {
        const values = {};
        for (let offset = 0; offset < handles.length; offset += disclosureBatchSize) {
            const batch = handles.slice(offset, offset + disclosureBatchSize);
            const request = await buildRequest(new URL(disclosurePath, this.endpoint), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ handles: batch }),
                signal: requestSignal,
            }, this.endpoint, state);
            assertNotAborted(requestSignal);
            this.assertActive();
            const guarded = guardRequestSignal(request, this.abortController.signal);
            try {
                const response = await this.baseFetch(guarded.request);
                assertNotAborted(requestSignal);
                this.assertActive();
                if (response.status === 429) {
                    throw new Error("Vallum disclosure budget is exhausted for this session");
                }
                if (!response.ok) {
                    throw new Error(`Vallum disclosure request failed with status ${response.status}`);
                }
                const payload = await response.json();
                assertNotAborted(requestSignal);
                this.assertActive();
                if (!isRecord(payload) || !Array.isArray(payload.results)) {
                    throw new Error("Vallum disclosure response is malformed");
                }
                for (const result of payload.results) {
                    if (!isRecord(result) || typeof result.handle !== "string")
                        continue;
                    if (typeof result.error === "string" && result.error)
                        continue;
                    // A render-only field arrives as pixels. It is never turned into a
                    // string here, so the plaintext does not enter the JS heap.
                    values[result.handle] = isRenderPayload(result.render)
                        ? new VallumRenderRef(result.render)
                        : result.value;
                }
            }
            finally {
                guarded.dispose();
            }
        }
        return values;
    }
    isRenderOnly(value) {
        return value instanceof VallumRenderRef;
    }
    async mount(element, value, options = {}) {
        this.assertActive();
        if (options.signal)
            assertNotAborted(options.signal);
        if (!(value instanceof VallumRenderRef))
            return false;
        const payload = value[inspectRenderReference]();
        if (!payload)
            return false;
        // The Go renderer intentionally emits raw (unpadded) base64. Normalize it
        // before `atob` so strict browser implementations accept every PNG size.
        const bytes = decodeBase64URL(payload.png);
        const blob = new Blob([bytes], { type: "image/png" });
        let bitmap;
        try {
            bitmap = await createImageBitmap(blob);
        }
        catch (error) {
            throw new Error("Vallum could not paint a render-only value", { cause: error });
        }
        try {
            this.assertActive();
            if (options.signal)
                assertNotAborted(options.signal);
        }
        catch (error) {
            bitmap.close();
            throw error;
        }
        try {
            const ratio = globalThis.devicePixelRatio ?? 1;
            const cssHeight = options.height ?? payload.height / 2;
            const cssWidth = (payload.width / payload.height) * cssHeight;
            const canvas = element.ownerDocument.createElement("canvas");
            canvas.width = Math.round(cssWidth * ratio);
            canvas.height = Math.round(cssHeight * ratio);
            canvas.style.width = `${cssWidth}px`;
            canvas.style.height = `${cssHeight}px`;
            canvas.style.verticalAlign = "middle";
            canvas.setAttribute("role", "img");
            canvas.setAttribute("data-vallum-render", "");
            canvas.setAttribute("data-vallum-source-width", String(payload.width));
            canvas.setAttribute("data-vallum-source-height", String(payload.height));
            // Without an explicit opt-in the value stays out of the accessibility
            // tree. That is the protection and also its accessibility cost.
            canvas.setAttribute("aria-label", options.accessibleLabel ?? "protected value");
            const context = canvas.getContext("2d");
            if (!context)
                throw new Error("Vallum could not obtain a 2D canvas context");
            context.imageSmoothingEnabled = true;
            context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
            this.assertActive();
            if (options.signal)
                assertNotAborted(options.signal);
            if (!value[consumeRenderReference](payload))
                return false;
            element.replaceChildren(canvas);
            return true;
        }
        finally {
            bitmap.close();
        }
    }
    commitCounter(replay, counter) {
        replay.seen.add(counter);
        replay.highest = Math.max(replay.highest, counter);
        const floor = replay.highest - responseReplayWindow;
        for (const seen of replay.seen)
            if (seen <= floor)
                replay.seen.delete(seen);
    }
    assertActive() {
        if (this.isDestroyed)
            throw new Error("Vallum client has been destroyed");
    }
}
function validateChallenge(challenge) {
    if (!challenge || challenge.version !== 1 || typeof challenge.session !== "string" || challenge.session.length < 8 ||
        typeof challenge.expires_at !== "string" || Number.isNaN(Date.parse(challenge.expires_at)) ||
        typeof challenge.admission_required !== "boolean" || typeof challenge.server_nonce !== "string" || challenge.server_nonce.length < 16) {
        throw new Error("Vallum session challenge is invalid");
    }
}
async function buildRequest(input, init, endpoint, state) {
    const source = input instanceof Request ? input : undefined;
    const rawURL = source?.url ?? (input instanceof URL ? input.toString() : String(input));
    const absoluteURL = new URL(rawURL, endpoint);
    const endpointURL = new URL(endpoint);
    if (absoluteURL.origin !== endpointURL.origin) {
        throw new TypeError("Vallum protected requests must target the configured endpoint origin");
    }
    const base = source ? new Request(absoluteURL, source) : new Request(absoluteURL, init);
    const request = source && init ? new Request(base, init) : base;
    const headers = new Headers(request.headers);
    headers.set(state.session_header, state.session);
    headers.set("Accept", headers.get("Accept") ?? "application/json");
    const unsigned = new Request(request, {
        headers,
        credentials: init?.credentials ?? source?.credentials ?? "include",
        cache: "no-store",
        redirect: "error",
    });
    const bodyHash = await requestBodyHash(unsigned);
    const tokenID = randomTokenID();
    const claims = {
        v: 1,
        s: state.session,
        k: state.key_id,
        m: unsigned.method.toUpperCase(),
        u: canonicalRequestURI(new URL(unsigned.url)),
        h: bodyHash,
        t: Date.now(),
        j: tokenID,
        n: state.server_nonce,
    };
    const payload = encodeBase64URL(encoder.encode(JSON.stringify(claims)));
    const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, state.proofKey, encoder.encode(requestProofDomain + payload));
    const signatureBytes = new Uint8Array(signature);
    if (signatureBytes.byteLength !== 64)
        throw new Error("Vallum request proof signature is invalid");
    headers.set(state.proof_header, `${payload}.${encodeBase64URL(signatureBytes)}`);
    return new Request(unsigned, {
        headers,
        credentials: init?.credentials ?? source?.credentials ?? "include",
        cache: "no-store",
        redirect: "error",
    });
}
/**
 * Couples a caller-owned request signal to the client's lifecycle without
 * replacing either one. Listeners remain attached only while fetch or body
 * reconstruction is in progress.
 */
function guardRequestSignal(request, lifecycleSignal) {
    const controller = new AbortController();
    const signals = request.signal === lifecycleSignal
        ? [lifecycleSignal]
        : [request.signal, lifecycleSignal];
    const listeners = [];
    for (const signal of signals) {
        const abort = () => {
            if (!controller.signal.aborted)
                controller.abort(signal.reason);
        };
        if (signal.aborted) {
            abort();
            break;
        }
        signal.addEventListener("abort", abort, { once: true });
        listeners.push({ signal, listener: abort });
    }
    return {
        request: new Request(request, { signal: controller.signal, redirect: "error" }),
        dispose() {
            for (const { signal, listener } of listeners)
                signal.removeEventListener("abort", listener);
        },
    };
}
function assertNotAborted(signal) {
    if (!signal.aborted)
        return;
    throw signal.reason ?? new DOMException("The operation was aborted", "AbortError");
}
async function requestBodyHash(request) {
    if (request.body === null)
        return unsignedPayloadMarker;
    let body;
    try {
        body = await request.clone().arrayBuffer();
    }
    catch (error) {
        throw new Error("Vallum cannot prove a non-cloneable request body", { cause: error });
    }
    if (body.byteLength === 0)
        return unsignedPayloadMarker;
    const digest = await crypto.subtle.digest("SHA-256", body);
    return encodeBase64URL(new Uint8Array(digest));
}
function canonicalRequestURI(url) {
    const path = canonicalPercentEscapes(url.pathname || "/");
    return path + (url.search ? `?${canonicalPercentEscapes(url.search.slice(1))}` : "");
}
function canonicalPercentEscapes(value) {
    return value.replace(/%[0-9a-fA-F]{2}/g, (escape) => escape.toUpperCase());
}
function randomTokenID() {
    const value = new Uint8Array(18);
    crypto.getRandomValues(value);
    return encodeBase64URL(value);
}
function parseEnvelope(metadata, profile) {
    if (profile === 0) {
        if (!isRecord(metadata) || !Array.isArray(metadata.c))
            throw new Error("Invalid Vallum profile 0 envelope");
        return envelopeValues(metadata.v, metadata.s, metadata.k, metadata.q, metadata.x, metadata.n, metadata.r, metadata.c.join(""));
    }
    if (profile === 1) {
        if (!Array.isArray(metadata) || metadata.length !== 8 || !Array.isArray(metadata[7]))
            throw new Error("Invalid Vallum profile 1 envelope");
        const chunks = [...metadata[7]].reverse().map((part) => {
            if (!Array.isArray(part) || typeof part[1] !== "string")
                throw new Error("Invalid Vallum profile 1 part");
            return part[1];
        });
        return envelopeValues(metadata[0], metadata[1], metadata[2], metadata[3], metadata[4], metadata[5], metadata[6], chunks.join(""));
    }
    if (!isRecord(metadata) || !isRecord(metadata.proof) || !isRecord(metadata.parts) || !Array.isArray(metadata.order)) {
        throw new Error("Invalid Vallum profile 2 envelope");
    }
    const chunks = metadata.order.map((name) => {
        if (typeof name !== "string" || typeof metadata.parts[name] !== "string")
            throw new Error("Invalid Vallum profile 2 part");
        return metadata.parts[name];
    });
    return envelopeValues(metadata.revision, metadata.subject, metadata.epoch, metadata.proof.counter, metadata.proof.until, metadata.proof.iv, metadata.route, chunks.join(""));
}
function envelopeValues(version, session, keyID, counter, expiresMS, nonce, routeTag, ciphertext) {
    if (!Number.isSafeInteger(version) || !Number.isSafeInteger(counter) || counter < 1 ||
        !Number.isSafeInteger(expiresMS) || typeof session !== "string" || typeof keyID !== "string" ||
        typeof nonce !== "string" || typeof routeTag !== "string" || typeof ciphertext !== "string") {
        throw new Error("Invalid Vallum envelope values");
    }
    return { version: version, session, keyID, counter: counter, expiresMS: expiresMS, nonce, routeTag, ciphertext };
}
function additionalData(envelope, profile) {
    const encoded = encoder.encode(`${envelope.version}\n${envelope.session}\n${envelope.keyID}\n${envelope.counter}\n${envelope.expiresMS}\n${profile}\n${envelope.routeTag}`);
    const output = new Uint8Array(new ArrayBuffer(encoded.byteLength));
    output.set(encoded);
    return output;
}
function validateBootstrap(value) {
    if (value.version !== 1 || value.algorithm !== "RSA-OAEP-256+A256GCM" ||
        !value.session || !value.key_id || !value.metadata_key || !value.wrapped_key || !value.server_nonce ||
        value.proof_header !== "X-Vallum-Proof" || value.session_header !== "X-Vallum-Session" ||
        ![0, 1, 2].includes(value.profile) || !Number.isFinite(Date.parse(value.expires_at))) {
        throw new Error("Vallum session bootstrap returned invalid material");
    }
}
function encodeBase64URL(value) {
    let binary = "";
    for (const item of value)
        binary += String.fromCharCode(item);
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function decodeBase64URL(value) {
    const padding = (4 - (value.length % 4)) % 4;
    const padded = value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(padding);
    const binary = atob(padded);
    const output = new Uint8Array(new ArrayBuffer(binary.length));
    for (let index = 0; index < binary.length; index++)
        output[index] = binary.charCodeAt(index);
    return output;
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isSafeRetry(method) {
    return method === "GET";
}
